import createClient from 'openapi-fetch'
import { ApiProblem, networkProblem, problemFromBody } from './problem'
import type { paths } from './schema'

/**
 * Единственная точка работы с API (ТЗ 18.2, 18.4). Типы запросов и ответов генерируются
 * из docs/openapi.json командой `npm run api:types`, расхождение с бэкендом ломает сборку.
 *
 * Access-токен живёт только в памяти. При 401 запрос повторяется один раз после общего
 * для всех параллельных запросов обновления токена (`/auth/refresh`).
 */
export interface AuthHooks {
  /** Текущий access-токен или null. */
  token(): string | null
  /** Обновляет сессию по refresh-cookie, возвращает новый токен или null. */
  refresh(): Promise<string | null>
  /** Обновить сессию не удалось: пользователя нужно вернуть ко входу. */
  expired(): void
  /** Сервер требует настроить 2FA (403 mfa_setup_required). */
  mfaSetupRequired(): void
}

let hooks: AuthHooks | null = null
let online: ((value: boolean) => void) | null = null

export function configureAuth(next: AuthHooks): void {
  hooks = next
}

/** Сетевой сбой и восстановление связи: для баннера «Нет соединения» (ТЗ 14.1). */
export function onConnectivity(listener: (online: boolean) => void): void {
  online = listener
}

/** Запросы, при которых 401 означает неверные данные, а не устаревший токен. */
const NO_RETRY = /\/api\/v1\/(auth\/|setup|invitations\/[^/]+$)/

async function authFetch(input: Request): Promise<Response> {
  const retry = input.clone()
  const token = hooks?.token() ?? null
  if (token !== null) input.headers.set('Authorization', `Bearer ${token}`)

  let response: Response
  try {
    response = await globalThis.fetch(input)
  } catch (error) {
    online?.(false)
    throw error
  }
  online?.(true)

  if (response.status === 401 && hooks !== null && !NO_RETRY.test(new URL(input.url).pathname)) {
    const fresh = await hooks.refresh()
    if (fresh === null) {
      hooks.expired()
      return response
    }
    retry.headers.set('Authorization', `Bearer ${fresh}`)
    response = await globalThis.fetch(retry)
  }
  if (
    response.status === 403 &&
    hooks !== null &&
    response.headers.get('Content-Type')?.includes('problem+json')
  ) {
    const body: unknown = await response
      .clone()
      .json()
      .catch(() => null)
    if (
      typeof body === 'object' &&
      body !== null &&
      (body as { code?: unknown }).code === 'mfa_setup_required'
    ) {
      hooks.mfaSetupRequired()
    }
  }

  return response
}

// Абсолютный адрес нужен Request вне браузера (тесты), в браузере это тот же источник.
const baseUrl = typeof location === 'undefined' ? '' : location.origin

export const api = createClient<paths>({ baseUrl, fetch: authFetch })

interface ApiResult<T> {
  data?: T
  error?: unknown
  response: Response
}

/**
 * Достаёт данные из ответа openapi-fetch или бросает ApiProblem: экраны работают
 * с исключениями, а TanStack Query показывает их как ошибки запроса.
 */
export async function unwrap<T>(request: Promise<ApiResult<T>>): Promise<T> {
  let result: ApiResult<T>
  try {
    result = await request
  } catch (error) {
    if (error instanceof ApiProblem) throw error
    throw networkProblem()
  }
  if (!result.response.ok) {
    throw problemFromBody(result.response.status, result.error, result.response.headers)
  }
  return result.data as T
}

export type HealthStatus = 'ok' | 'unavailable'

export async function fetchHealth(): Promise<HealthStatus> {
  try {
    const { data, error } = await api.GET('/api/v1/health')
    if (error !== undefined || data === undefined) return 'unavailable'
    return data.status === 'ok' ? 'ok' : 'unavailable'
  } catch {
    return 'unavailable'
  }
}
